import React from 'react';
import { format } from 'date-fns';

const ProfessionalBragSheetView = ({
  bragSheetData,
  isTeacherSpecific = false,
  teacherInfo = {},
  studentInfo = {},
  compactMode = false
}) => {
  const {
    basicInfo = {},
    academicHighlights = {},
    extracurriculars = [],
    achievements = [],
    collegeGoals = '',
    personalBrand = '',
    leadershipContexts = {},
    unexpectedDetails = [],
    classroomMoments = [],
    thankYouNote = '',
    sectionExclusions = {}
  } = bragSheetData;

  // Calculate current year for graduation year display
  const currentYear = new Date().getFullYear();
  const graduationYear = basicInfo.graduationYear || (currentYear + (13 - parseInt(basicInfo.gradeLevel || '12')));

  return (
    <>
      <style>{`
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .professional-brag-sheet {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          /* Ensure all backgrounds print */
          div[style*="background"] {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          /* Hide page break indicators in print */
          .page-break::before,
          .page-break::after {
            display: none !important;
          }
        }

        /* Visual page separation in preview only (not in print) */
        @media screen {
          .page-break {
            margin-top: 80px !important;
            padding-top: 60px !important;
            position: relative;
          }

          /* Page break indicator - dashed line across page */
          .page-break::before {
            content: "";
            position: absolute;
            top: -40px;
            left: -0.75in;
            right: -0.75in;
            height: 0;
            border-top: 3px dashed #2563eb;
            z-index: 10;
          }

          /* Page break label */
          .page-break::after {
            content: "PAGE BREAK";
            position: absolute;
            top: -52px;
            left: 50%;
            transform: translateX(-50%);
            padding: 6px 20px;
            background: #2563eb;
            color: white;
            border-radius: 4px;
            font-size: 11px;
            font-family: system-ui, -apple-system, sans-serif;
            text-transform: uppercase;
            letter-spacing: 1.5px;
            font-weight: 700;
            white-space: nowrap;
            z-index: 11;
            box-shadow: 0 2px 4px rgba(37, 99, 235, 0.3);
          }
        }

        /* Compact mode styles - fit more content per page */
        .compact-mode {
          padding: 0.4in !important;
        }

        @media screen {
          .compact-mode .page-break {
            margin-top: 50px !important;
            padding-top: 40px !important;
          }

          .compact-mode .page-break::before {
            top: -30px;
            left: -0.4in;
            right: -0.4in;
          }

          .compact-mode .page-break::after {
            top: -42px;
            font-size: 10px;
            padding: 5px 16px;
          }
        }
      `}</style>
      <div className={`professional-brag-sheet ${compactMode ? 'compact-mode' : ''}`} style={{ fontFamily: 'Georgia, serif', maxWidth: '8.5in', margin: '0 auto', padding: '0.75in', background: 'white', color: '#2c3e50', lineHeight: 1.6 }}>

      {/* Teacher Header Bar (if teacher-specific) */}
      {isTeacherSpecific && (
        <div style={{ borderTop: '2px solid #000', borderBottom: '2px solid #000', padding: '0.8rem', marginBottom: '2rem', textAlign: 'center' }}>
          <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.3rem' }}>
            For {teacherInfo.name || 'Teacher'} - {teacherInfo.subject || 'Subject'}
          </div>
          <div style={{ fontSize: '0.9rem', color: '#2c3e50' }}>
            Recommendation Deadline: {teacherInfo.deadline || 'TBD'}
          </div>
        </div>
      )}

      {/* Student Header */}
      <div style={{ textAlign: 'center', paddingBottom: '1rem', marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2c3e50', marginBottom: '0.5rem' }}>
          {basicInfo.name || 'Student Name'}
        </h1>
        <div style={{ color: '#7f8c8d', fontSize: '1rem', opacity: 0.9 }}>
          {basicInfo.gradeLevel ? `${basicInfo.gradeLevel}th Grade` : ''} • {basicInfo.school || 'School'} • Class of {graduationYear}
        </div>

        {/* In My Own Words */}
        {!sectionExclusions?.personalBrand && personalBrand && (
          <div style={{ background: '#f6f6f6', padding: '1.5rem', margin: '1.5rem 0 0.5rem', borderRadius: '6px', fontStyle: 'italic', fontSize: '1.1rem', lineHeight: 1.7 }}>
            <h4 style={{ margin: '0 0 1rem 0', fontStyle: 'normal', fontWeight: 'bold', color: '#000' }}>
              In My Own Words
            </h4>
            "{personalBrand}"
          </div>
        )}
      </div>

      {/* Academic Overview Section */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2c3e50', margin: '2rem 0 1.5rem 0', borderBottom: '2px solid #bdc3c7', paddingBottom: '0.5rem' }}>
          Academic Overview
        </div>

        {/* Stats Grid - First Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', margin: '1.5rem 0' }}>
          <div style={{ background: '#f8f8f8', border: '1px solid #000', borderRadius: '6px', padding: '1.5rem', textAlign: 'center' }}>
            <div style={{ color: '#000', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '0.5rem' }}>
              GPA
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#000', marginBottom: '0.3rem' }}>
              {academicHighlights.gpa?.split(' ')[0] || 'N/A'}
            </div>
            <div style={{ color: '#2c3e50', fontSize: '0.9rem' }}>
              Weighted (4.0 scale)
            </div>
          </div>

          <div style={{ background: '#f8f8f8', border: '1px solid #000', borderRadius: '6px', padding: '1.5rem', textAlign: 'center' }}>
            <div style={{ color: '#000', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '0.5rem' }}>
              Class Rank
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#000', marginBottom: '0.3rem' }}>
              {academicHighlights.classRank ? academicHighlights.classRank.split('/')[0] : 'N/A'}
            </div>
            <div style={{ color: '#2c3e50', fontSize: '0.9rem' }}>
              of {academicHighlights.classRank ? academicHighlights.classRank.split('/')[1] : 'class'}
            </div>
          </div>

          <div style={{ background: '#f8f8f8', border: '1px solid #000', borderRadius: '6px', padding: '1.5rem', textAlign: 'center' }}>
            <div style={{ color: '#000', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '0.5rem' }}>
              Test Scores
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#000', marginBottom: '0.3rem' }}>
              {academicHighlights.testScores || 'N/A'}
            </div>
            <div style={{ color: '#2c3e50', fontSize: '0.9rem' }}>
              SAT/ACT
            </div>
          </div>
        </div>

        {/* Secondary Stats - Second Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', margin: '1.5rem 0' }}>
          <div style={{ background: '#f8f8f8', border: '1px solid #000', borderRadius: '6px', padding: '1.5rem', textAlign: 'center' }}>
            <div style={{ color: '#000', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '0.5rem' }}>
              AP/Honors
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#000', marginBottom: '0.3rem' }}>
              {academicHighlights.courseLoad?.match(/(\d+)/)?.[0] || '0'}
            </div>
            <div style={{ color: '#2c3e50', fontSize: '0.9rem' }}>
              Advanced Courses
            </div>
          </div>

          <div style={{ background: '#f8f8f8', border: '1px solid #000', borderRadius: '6px', padding: '1.5rem', textAlign: 'center' }}>
            <div style={{ color: '#000', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '0.5rem' }}>
              Academic Focus
            </div>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#000', marginBottom: '0.3rem' }}>
              {basicInfo.intendedMajor || 'Undecided'}
            </div>
            <div style={{ color: '#2c3e50', fontSize: '0.9rem' }}>
              Primary Interest
            </div>
          </div>

          <div style={{ background: '#f8f8f8', border: '1px solid #000', borderRadius: '6px', padding: '1.5rem', textAlign: 'center' }}>
            <div style={{ color: '#000', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '0.5rem' }}>
              Activities
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#000', marginBottom: '0.3rem' }}>
              {extracurriculars.length}
            </div>
            <div style={{ color: '#2c3e50', fontSize: '0.9rem' }}>
              Leadership & Service
            </div>
          </div>
        </div>
      </div>

      {/* Extracurricular Activities */}
      {!sectionExclusions?.extracurriculars && extracurriculars.length > 0 && (
        <div className="page-break" style={{ margin: '3rem 0' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2c3e50', margin: '2rem 0 1.5rem 0', borderBottom: '2px solid #bdc3c7', paddingBottom: '0.5rem' }}>
            Extracurricular Activities
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', margin: '1.5rem 0' }}>
            {extracurriculars.map((activity, index) => (
              <div key={index} style={{ background: 'white', border: '1px solid #000', borderRadius: '6px', padding: '1.25rem' }}>
                <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#000', marginBottom: '0.5rem' }}>
                  {activity.activity || activity.name}
                </div>
                {activity.role && (
                  <div style={{ fontSize: '0.95rem', color: '#2c3e50', fontStyle: 'italic', marginBottom: '0.5rem' }}>
                    {activity.role}
                  </div>
                )}
                <div style={{ fontSize: '0.95rem', color: '#2c3e50', lineHeight: 1.5, marginBottom: '0.75rem' }}>
                  {activity.description || activity.achievements || activity.impact || 'Active participant and contributor'}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.75rem' }}>
                  {activity.yearsInvolved && (
                    <span style={{ background: '#f0f0f0', color: '#000', padding: '0.3rem 0.6rem', borderRadius: '3px', fontSize: '0.8rem', fontWeight: '500' }}>
                      {activity.yearsInvolved} years
                    </span>
                  )}
                  {activity.timeCommitment && (
                    <span style={{ background: '#f0f0f0', color: '#000', padding: '0.3rem 0.6rem', borderRadius: '3px', fontSize: '0.8rem', fontWeight: '500' }}>
                      {activity.timeCommitment}
                    </span>
                  )}
                  {(activity.hoursPerWeek || activity.weeksPerYear) && (
                    <span style={{ background: '#f0f0f0', color: '#000', padding: '0.3rem 0.6rem', borderRadius: '3px', fontSize: '0.8rem', fontWeight: '500' }}>
                      {activity.hoursPerWeek && `${activity.hoursPerWeek} hrs/week`}
                      {activity.hoursPerWeek && activity.weeksPerYear && ' • '}
                      {activity.weeksPerYear && `${activity.weeksPerYear} weeks/year`}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* College Goals Section */}
      {!sectionExclusions?.collegeGoals && collegeGoals && (
        <div style={{ margin: '3rem 0' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2c3e50', margin: '2rem 0 1.5rem 0', borderBottom: '2px solid #bdc3c7', paddingBottom: '0.5rem' }}>
            College Goals
          </div>
          <div style={{ lineHeight: 1.6 }}>
            {collegeGoals}
          </div>
        </div>
      )}

      {/* Classroom Moments (Teacher-Specific) */}
      {isTeacherSpecific && !sectionExclusions?.classroomMoments && classroomMoments.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2c3e50', margin: '2rem 0 1.5rem 0', borderBottom: '2px solid #bdc3c7', paddingBottom: '0.5rem' }}>
            Specific Classroom Moments for Your Reference
          </div>
          <div style={{ background: '#f8f8f8', border: '1px solid #000', borderRadius: '6px', padding: '1.5rem', margin: '1.5rem 0' }}>
            {classroomMoments.map((moment, index) => (
              <div key={index} style={{ marginBottom: '1.8rem', paddingBottom: '1.5rem', borderBottom: index < classroomMoments.length - 1 ? '1px solid #bdc3c7' : 'none' }}>
                <div style={{ fontWeight: 'bold', color: '#000', marginBottom: '0.8rem', fontSize: '1.05rem' }}>
                  {moment.title}
                </div>
                <div style={{ color: '#2c3e50', lineHeight: 1.6 }}>
                  {moment.description}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Leadership & Character Experiences */}
      {!sectionExclusions?.leadershipContexts && Object.keys(leadershipContexts).length > 0 && (
        <div className="page-break" style={{ margin: '2rem 0' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2c3e50', margin: '2rem 0 1.5rem 0', borderBottom: '2px solid #bdc3c7', paddingBottom: '0.5rem' }}>
            Leadership & Character Experiences
          </div>
          {Object.entries(leadershipContexts).map(([contextId, description]) => {
            if (!description || !description.trim()) return null;
            const contextLabel = contextId.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            return (
              <div key={contextId} style={{ border: '1px solid #000', borderLeft: '4px solid #000', padding: '1.2rem 1.5rem', margin: '1rem 0' }}>
                <h4 style={{ color: '#000', fontWeight: 'bold', margin: '0 0 0.8rem 0', fontSize: '1.1rem' }}>
                  {contextLabel}
                </h4>
                <p style={{ margin: 0, lineHeight: 1.6 }}>{description}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Achievements Box */}
      {!sectionExclusions?.achievements && achievements.length > 0 && (
        <div style={{ background: '#f8f8f8', borderRadius: '6px', padding: '1.5rem', margin: '1.5rem 0', textAlign: 'center' }}>
          <h3 style={{ color: '#000', fontSize: '1.2rem', margin: '0 0 1rem 0', fontWeight: 'bold' }}>
            Academic Recognition
          </h3>
          {achievements.map((achievement, index) => (
            <p key={index} style={{ margin: '0.5rem 0', color: '#2c3e50' }}>
              {achievement.title}
            </p>
          ))}
        </div>
      )}

      {/* Things You Might Not Know */}
      {!sectionExclusions?.unexpectedDetails && unexpectedDetails.length > 0 && (
        <div style={{ margin: '2rem 0' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2c3e50', margin: '2rem 0 1.5rem 0', borderBottom: '2px solid #bdc3c7', paddingBottom: '0.5rem' }}>
            A few things you might not know about me
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', margin: '1rem 0' }}>
            {unexpectedDetails.map((detail, index) => (
              <div key={index} style={{ padding: '1rem', background: '#f8f8f8' }}>
                <h4 style={{ lineHeight: 1.2, marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  {detail.title}
                </h4>
                <p style={{ fontSize: '0.9rem', color: '#2c3e50', margin: 0 }}>
                  {detail.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Timeline of Growth */}
      {(extracurriculars.length > 0 || achievements.length > 0) && (
        <div className="page-break" style={{ margin: '3rem 0' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2c3e50', margin: '2rem 0 1.5rem 0', borderBottom: '2px solid #bdc3c7', paddingBottom: '0.5rem' }}>
            Timeline of Growth
          </div>

          <div style={{ marginLeft: '2rem', borderLeft: '3px solid #bdc3c7', paddingLeft: '2rem' }}>
            {(() => {
              // Count activities by year for thematic summary
              const activitiesByYear = { '9': 0, '10': 0, '11': 0, '12': 0 };
              extracurriculars.forEach(activity => {
                const years = activity.yearsInvolved || activity.yearsParticipated || '';
                ['9', '10', '11', '12'].forEach(year => {
                  if (years.includes(year)) activitiesByYear[year]++;
                });
              });

              const currentGrade = parseInt(basicInfo.gradeLevel) || 12;
              const years = [
                { name: 'Freshman', grade: 9, theme: 'Foundation Building' },
                { name: 'Sophomore', grade: 10, theme: 'Exploration & Growth' },
                { name: 'Junior', grade: 11, theme: 'Leadership & Achievement' },
                { name: 'Senior', grade: 12, theme: 'Culmination & Vision' }
              ];

              return years.filter(y => y.grade <= currentGrade).map(({ name, grade, theme }) => {
                const count = activitiesByYear[grade.toString()];
                const leadershipCount = extracurriculars.filter(a =>
                  a.role && (a.yearsInvolved || a.yearsParticipated || '').includes(grade.toString())
                ).length;

                let description = '';
                if (grade === 9) {
                  description = count > 0
                    ? `Building academic foundation while engaging in ${count} extracurricular ${count === 1 ? 'activity' : 'activities'}`
                    : 'Beginning high school journey, building foundation';
                } else if (grade === 10) {
                  description = count > 0
                    ? `Deepening involvement across ${count} ${count === 1 ? 'activity' : 'activities'}, discovering passions and strengths`
                    : 'Expanding interests and involvement';
                } else if (grade === 11) {
                  description = leadershipCount > 0
                    ? `Taking on leadership roles in ${leadershipCount} ${leadershipCount === 1 ? 'activity' : 'activities'}, making significant impact`
                    : count > 0
                    ? `Continuing commitment across ${count} ${count === 1 ? 'activity' : 'activities'}`
                    : 'Taking on leadership roles and making impact';
                  if (achievements.length > 0) {
                    description += `. ${achievements.map(a => a.title).join(', ')}`;
                  }
                } else {
                  description = count > 0
                    ? `Mentoring others and applying leadership across ${count} ${count === 1 ? 'activity' : 'activities'}, preparing for future impact`
                    : 'Preparing for college and beyond';
                }

                return (
                  <div key={name} style={{ marginBottom: '2rem', position: 'relative' }}>
                    <div style={{ position: 'absolute', left: '-2.5rem', top: '0', width: '1rem', height: '1rem', borderRadius: '50%', background: '#2c3e50', border: '3px solid white' }}></div>
                    <div style={{ fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '0.5rem' }}>{name} Year</div>
                    <div style={{ fontSize: '0.95rem', fontWeight: '600', color: '#555', marginBottom: '0.3rem' }}>{theme}</div>
                    <p style={{ fontSize: '0.9rem', color: '#666', lineHeight: 1.5 }}>{description}</p>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {/* Contact Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', margin: '2rem 0 1rem', padding: '1.5rem', borderTop: '2px solid #000', borderBottom: '2px solid #000' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#000', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '0.5rem' }}>
            Email
          </div>
          <div style={{ color: '#2c3e50', fontWeight: '500' }}>
            {basicInfo.email || studentInfo.email || 'student@email.com'}
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#000', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '0.5rem' }}>
            Phone
          </div>
          <div style={{ color: '#2c3e50', fontWeight: '500' }}>
            {basicInfo.phone || studentInfo.phone || '(555) 000-0000'}
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#000', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '0.5rem' }}>
            Location
          </div>
          <div style={{ color: '#2c3e50', fontWeight: '500' }}>
            {basicInfo.location || studentInfo.location || 'City, State'}
          </div>
        </div>
      </div>

      {/* Thank You Note (Teacher-Specific) */}
      {isTeacherSpecific && !sectionExclusions?.thankYouNote && thankYouNote && (
        <div style={{ margin: '2rem 0', fontStyle: 'italic', color: '#2c3e50', lineHeight: 1.6 }}>
          {thankYouNote}
        </div>
      )}
    </div>
    </>
  );
};

export default ProfessionalBragSheetView;